import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Studio from "@/pages/Studio";
import ClipLab from "@/pages/ClipLab";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Policies from "@/pages/Policies";
import PaymentSuccess from "@/pages/PaymentSuccess";
import UnderConstruction from "@/pages/UnderConstruction";
import { AuraMark } from "@/components/AuraMark";
import { MobileNotice } from "@/components/MobileNotice";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[var(--aura-bg)]">
    <AuraMark className="w-12 h-12 floaty" />
  </div>
);

const Protected = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !["admin", "super_admin"].includes(user.role))
    return <Navigate to="/dashboard" replace />;
  return children;
};

// Paths always reachable even while the public site is under construction
const UC_ALLOWED = ["/login"];

function AppRouter() {
  const location = useLocation();
  const { user, loading, underConstruction, settingsLoading } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const privileged = user && ["admin", "super_admin"].includes(user.role);
  if (underConstruction && !privileged) {
    if (settingsLoading || loading) return <Loader />;
    if (!UC_ALLOWED.includes(location.pathname)) return <UnderConstruction />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/policies" element={<Policies />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/studio/:pid" element={<Protected><Studio /></Protected>} />
      <Route path="/projects/:pid/clip-lab" element={<Protected><ClipLab /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App aura-grain">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <MobileNotice />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
